/**
 * PMT product page — <pmt-buy-box> controller for the pmt-single and pmt-bundle templates.
 * Owns supply tier, purchase mode and selling plan; keeps prices, discount pills and form
 * inputs in sync.
 *
 * This is a deliberate fork of assets/pmt-pdp.js rather than a shared module. pmt-pdp.js
 * serves templates/product.pmt.json, a live CRO control that is frozen until the test ends,
 * and it carries the Appstle /cart/add bypass that protects real revenue. Forking keeps that
 * file untouched and lets this one diverge; both are deleted together when the control retires.
 * Divergence from pmt-pdp.js is limited to paintBadge() and its call site in recompute().
 */

class PmtBuyBox extends HTMLElement {
  connectedCallback() {
    this.state = {
      tier: 1,
      tierDiscount: 0,
      subscription: false,
      planDiscount: 0,
      sellingPlanId: '',
    };

    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
    this.tiersRoot = this.querySelector('[data-pmt-tiers]');
    this.saveLabel = (this.tiersRoot && this.tiersRoot.dataset.saveLabel) || 'Save';
    this.tierEls = Array.from(this.querySelectorAll('[data-pmt-tier]'));
    this.optionsRoot = this.querySelector('[data-pmt-options]');
    this.modeEls = Array.from(this.querySelectorAll('[data-pmt-mode]'));
    this.frequencySelect = this.querySelector('[data-pmt-frequency]');
    this.quantityInput = this.querySelector('[data-pmt-quantity-input]');
    this.planInput = this.querySelector('[data-pmt-selling-plan-input]');
    this.subPriceEl = this.querySelector('[data-pmt-sub-price]');
    this.subCompareEl = this.querySelector('[data-pmt-sub-compare]');
    this.onetimePriceEl = this.querySelector('[data-pmt-onetime-price]');
    this.atcButton = this.querySelector('[data-pmt-atc]');
    this.form = this.atcButton ? this.atcButton.form : null;

    const currency = this.dataset.currency || 'USD';
    this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
    });

    this.initFromDom();
    this.bindTiers();
    this.bindModes();
    this.bindFrequency();
    this.bindAddToCart();
    this.bindReadMore();
    this.guardSellingPlanInputs();
    this.recompute();
  }

  disconnectedCallback() {
    if (this.planObserver) this.planObserver.disconnect();
    if (this.planObserverTimer) window.clearTimeout(this.planObserverTimer);
    if (this.cartUpdateHandler) {
      document.removeEventListener('cart:update', this.cartUpdateHandler);
      document.removeEventListener('cart:error', this.cartUpdateHandler);
    }
  }

  initFromDom() {
    const selectedTier = this.tierEls.find((el) => el.classList.contains('is-selected')) || this.tierEls[0];
    if (selectedTier) {
      this.state.tier = parseInt(selectedTier.dataset.qty, 10) || 1;
      this.state.tierDiscount = parseFloat(selectedTier.dataset.discount) || 0;
    }

    const selectedMode = this.modeEls.find((el) => el.getAttribute('aria-checked') === 'true');
    this.state.subscription = selectedMode ? selectedMode.dataset.pmtMode === 'subscription' : false;

    this.readFrequency();
  }

  readFrequency() {
    if (!this.frequencySelect) return;
    const option = this.frequencySelect.options[this.frequencySelect.selectedIndex];
    this.state.planDiscount = option ? parseFloat(option.dataset.discount) || 0 : 0;
    this.state.sellingPlanId = option ? option.value : '';
  }

  bindTiers() {
    this.tierEls.forEach((el) => {
      el.addEventListener('click', () => this.selectTier(el));
      el.addEventListener('keydown', (event) => this.handleRadioKeydown(event, this.tierEls, el, (target) => this.selectTier(target)));
    });
  }

  bindModes() {
    this.modeEls.forEach((el) => {
      el.addEventListener('click', (event) => {
        if (event.target.closest('[data-pmt-frequency]')) return;
        this.selectMode(el);
      });
      el.addEventListener('keydown', (event) => {
        if (event.target.closest('[data-pmt-frequency]')) return;
        this.handleRadioKeydown(event, this.modeEls, el, (target) => this.selectMode(target));
      });
    });
  }

  bindFrequency() {
    if (!this.frequencySelect) return;
    this.frequencySelect.addEventListener('change', () => {
      this.readFrequency();
      if (!this.state.subscription) {
        const subCard = this.modeEls.find((el) => el.dataset.pmtMode === 'subscription');
        if (subCard) this.selectMode(subCard);
        return;
      }
      this.syncInputs();
      this.recompute();
    });
  }

  bindAddToCart() {
    if (!this.form || !this.atcButton) return;

    this.form.addEventListener('submit', () => {
      this.wasSubmitted = true;
      this.atcButton.classList.add('is-loading');
      window.setTimeout(() => this.atcButton.classList.remove('is-loading'), 8000);
    });

    const label = this.atcButton.querySelector('.pmt-buy__atc-label');
    const defaultText = label ? label.textContent : '';
    const addedText = this.atcButton.dataset.addedText || '';

    this.cartUpdateHandler = (event) => {
      this.atcButton.classList.remove('is-loading');
      if (event.type === 'cart:update' && this.wasSubmitted && label && addedText) {
        this.wasSubmitted = false;
        label.textContent = addedText;
        this.atcButton.classList.add('is-added');
        window.clearTimeout(this.addedTimer);
        this.addedTimer = window.setTimeout(() => {
          label.textContent = defaultText;
          this.atcButton.classList.remove('is-added');
        }, 2000);
      }
    };
    document.addEventListener('cart:update', this.cartUpdateHandler);
    document.addEventListener('cart:error', this.cartUpdateHandler);
  }

  bindReadMore() {
    const button = this.querySelector('[data-pmt-read-more]');
    if (!button) return;
    const moreLabel = button.textContent.trim();
    const lessLabel = button.dataset.lessLabel || 'READ LESS';
    button.addEventListener('click', () => {
      const expanded = button.getAttribute('aria-expanded') === 'true';
      this.querySelectorAll('[data-pmt-bullet-extra]').forEach((item) => {
        item.hidden = expanded;
      });
      button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      button.textContent = expanded ? moreLabel : lessLabel;
    });
  }

  // Third-party embeds inject their own input[name="selling_plan"]; a later empty one wins in FormData.
  guardSellingPlanInputs() {
    if (!this.form || !this.planInput) return;

    const neutralize = () => {
      const inputs = this.form.querySelectorAll('input[name="selling_plan"]');
      const external = document.querySelectorAll(`input[name="selling_plan"][form="${this.form.id}"]`);
      [...inputs, ...external].forEach((input) => {
        if (input === this.planInput) return;
        input.disabled = true;
        input.removeAttribute('name');
        input.setAttribute('data-pmt-neutralized', '');
      });
    };

    neutralize();

    this.planObserver = new MutationObserver(() => {
      if (this.neutralizeQueued) return;
      this.neutralizeQueued = true;
      requestAnimationFrame(() => {
        this.neutralizeQueued = false;
        neutralize();
      });
    });
    this.planObserver.observe(this.form, { childList: true, subtree: true });

    this.planObserverTimer = window.setTimeout(() => {
      this.planObserver.disconnect();
      this.planObserver = null;
    }, 15000);

    this.form.addEventListener('submit', neutralize, { capture: true });
  }

  selectTier(el) {
    this.tierEls.forEach((tierEl) => {
      const selected = tierEl === el;
      tierEl.classList.toggle('is-selected', selected);
      tierEl.setAttribute('aria-checked', selected ? 'true' : 'false');
      tierEl.setAttribute('tabindex', selected ? '0' : '-1');
    });
    this.state.tier = parseInt(el.dataset.qty, 10) || 1;
    this.state.tierDiscount = parseFloat(el.dataset.discount) || 0;
    this.syncInputs();
    this.recompute();
  }

  selectMode(el) {
    this.modeEls.forEach((modeEl) => {
      const selected = modeEl === el;
      modeEl.classList.toggle('is-selected', selected);
      modeEl.setAttribute('aria-checked', selected ? 'true' : 'false');
      modeEl.setAttribute('tabindex', selected ? '0' : '-1');
      if (modeEl.dataset.pmtMode === 'one-time') {
        modeEl.classList.toggle('is-dimmed', !selected);
      }
    });
    this.state.subscription = el.dataset.pmtMode === 'subscription';
    this.readFrequency();
    this.syncInputs();
    this.recompute();
  }

  handleRadioKeydown(event, group, current, select) {
    const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ', 'Enter'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();

    if (event.key === ' ' || event.key === 'Enter') {
      select(current);
      return;
    }

    const index = group.indexOf(current);
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const nextIndex = (index + (forward ? 1 : -1) + group.length) % group.length;
    const next = group[nextIndex];
    select(next);
    next.focus();
  }

  syncInputs() {
    if (this.quantityInput) this.quantityInput.value = this.state.tier;
    if (this.planInput) {
      this.planInput.value = this.state.subscription ? this.state.sellingPlanId : '';
    }
  }

  // Rounds the subscription unit price first, to match Shopify checkout math.
  priceFor(qty, tierDiscount, subscribed) {
    const unit = subscribed
      ? Math.round(this.basePrice * (1 - this.state.planDiscount / 100))
      : this.basePrice;
    return Math.round(unit * qty * (1 - tierDiscount / 100));
  }

  recompute() {
    const { tier, tierDiscount, subscription } = this.state;

    this.tierEls.forEach((el) => {
      const qty = parseInt(el.dataset.qty, 10) || 1;
      const rowDiscount = parseFloat(el.dataset.discount) || 0;
      const strike = this.basePrice * qty;
      const price = this.priceFor(qty, rowDiscount, subscription);
      const savings = strike - price;

      const priceEl = el.querySelector('[data-pmt-tier-price]');
      const strikeEl = el.querySelector('[data-pmt-tier-strike]');
      const saveEl = el.querySelector('[data-pmt-tier-save]');
      const badgeEl = el.querySelector('[data-pmt-tier-badge]');

      if (priceEl) priceEl.textContent = this.formatMoney(price);
      if (strikeEl) {
        strikeEl.textContent = this.formatMoney(strike);
        strikeEl.hidden = savings <= 0;
      }
      if (saveEl) {
        saveEl.textContent = `${this.saveLabel} ${this.formatMoney(savings)}`;
        saveEl.hidden = savings <= 0;
      }
      this.paintBadge(badgeEl, strike, savings);
    });

    const onetimeTotal = this.priceFor(tier, tierDiscount, false);
    const subTotal = this.priceFor(tier, tierDiscount, true);
    // The subscription card compares against the full base price — same anchor as the tier rows' strike price.
    const subCompareTotal = this.basePrice * tier;

    if (this.subPriceEl) this.subPriceEl.textContent = this.formatMoney(subTotal);
    if (this.subCompareEl) {
      this.subCompareEl.textContent = this.formatMoney(subCompareTotal);
      this.subCompareEl.style.display = subTotal < subCompareTotal ? '' : 'none';
    }
    if (this.onetimePriceEl) this.onetimePriceEl.textContent = this.formatMoney(onetimeTotal);
  }

  /**
   * Repaints a tier's discount pill from the prices actually on screen.
   *
   * The pill must track the purchase mode: a tier with no tier-level discount still saves the
   * subscription percentage while Subscribe is selected, and saves nothing once One-time is.
   * Leaving it at its server-rendered value advertises a discount the shopper will not get.
   */
  paintBadge(badgeEl, strike, savings) {
    if (!badgeEl) return;
    const percent = strike > 0 ? Math.round((savings / strike) * 100) : 0;
    const percentEl = badgeEl.querySelector('[data-pmt-tier-badge-percent]') || badgeEl;
    percentEl.textContent = `-${percent}% OFF`;
    badgeEl.hidden = percent <= 0;
  }

  formatMoney(cents) {
    return this.moneyFormatter.format(cents / 100);
  }
}

if (!customElements.get('pmt-buy-box')) {
  customElements.define('pmt-buy-box', PmtBuyBox);
}

/** <pmt-review-slider> — one review per slide, with arrows, dots and touch swipe. */
class PmtReviewSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-pmt-slide]'));
    this.controls = this.querySelector('[data-pmt-controls]');
    this.dotsWrap = this.querySelector('[data-pmt-dots]');
    this.index = 0;

    if (this.slides.length < 2) return;

    if (this.controls) this.controls.hidden = false;
    this.buildDots();

    this.querySelector('[data-pmt-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-pmt-next]')?.addEventListener('click', () => this.go(this.index + 1));

    const viewport = this.querySelector('[data-pmt-slides]');
    if (viewport) {
      let startX = null;
      viewport.addEventListener('touchstart', (event) => {
        startX = event.touches[0].clientX;
      }, { passive: true });
      viewport.addEventListener('touchend', (event) => {
        if (startX === null) return;
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) this.go(this.index + (delta < 0 ? 1 : -1));
        startX = null;
      }, { passive: true });
    }
  }

  buildDots() {
    if (!this.dotsWrap) return;
    this.dots = this.slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pmt-reviews__dot';
      dot.setAttribute('aria-label', `Go to review ${i + 1}`);
      dot.addEventListener('click', () => this.go(i));
      this.dotsWrap.appendChild(dot);
      return dot;
    });
    this.paint();
  }

  go(nextIndex) {
    const count = this.slides.length;
    this.index = ((nextIndex % count) + count) % count;
    this.paint();
  }

  paint() {
    this.slides.forEach((slide, i) => {
      slide.hidden = i !== this.index;
    });
    if (this.dots) {
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    }
  }
}

if (!customElements.get('pmt-review-slider')) {
  customElements.define('pmt-review-slider', PmtReviewSlider);
}

/** Shared photo overlay for review photos — prev/next, keyboard and click-outside close. */
class PmtReviewLightbox {
  constructor() {
    this.el = null;
    this.track = null;
    this.images = [];
    this.index = 0;
    this.boundKeydown = this.onKeydown.bind(this);
  }

  ensureBuilt() {
    if (this.el) return;

    this.el = document.createElement('div');
    this.el.className = 'pmt-lightbox';
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-modal', 'true');
    this.el.setAttribute('aria-label', 'Customer photos');
    this.el.hidden = true;

    this.el.innerHTML = `
      <button type="button" class="pmt-lightbox__close" data-pmt-lightbox-close aria-label="Close">
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <button type="button" class="pmt-lightbox__arrow pmt-lightbox__arrow--prev" data-pmt-lightbox-prev aria-label="Previous photo">
        <img src="${window.pmtAssets?.arrowIcon || ''}" alt="" width="32" height="32">
      </button>
      <div class="pmt-lightbox__viewport">
        <div class="pmt-lightbox__track" data-pmt-lightbox-track></div>
      </div>
      <button type="button" class="pmt-lightbox__arrow pmt-lightbox__arrow--next" data-pmt-lightbox-next aria-label="Next photo">
        <img src="${window.pmtAssets?.arrowIcon || ''}" alt="" width="32" height="32">
      </button>
    `;
    document.body.appendChild(this.el);
    this.track = this.el.querySelector('[data-pmt-lightbox-track]');

    this.el.querySelector('[data-pmt-lightbox-close]').addEventListener('click', () => this.close());
    this.el.querySelector('[data-pmt-lightbox-prev]').addEventListener('click', () => this.go(this.index - 1));
    this.el.querySelector('[data-pmt-lightbox-next]').addEventListener('click', () => this.go(this.index + 1));

    this.el.addEventListener('click', (event) => {
      if (event.target === this.el) this.close();
    });
  }

  open(slide, sourceImg) {
    const photos = Array.from(slide.querySelectorAll('.pmt-reviews__photos img'));
    if (photos.length === 0) return;

    this.ensureBuilt();

    this.track.innerHTML = '';
    this.images = photos.map((img) => {
      const src = img.currentSrc || img.src;
      return src.replace(/_(\d+)x(\.\w+)(?:\?|$)/, '_800x$2$3');
    });
    this.images.forEach((src) => {
      const cell = document.createElement('div');
      cell.className = 'pmt-lightbox__cell';
      const pic = document.createElement('img');
      pic.src = src;
      pic.alt = '';
      pic.loading = 'lazy';
      cell.appendChild(pic);
      this.track.appendChild(cell);
    });

    this.index = Math.max(0, Math.min(photos.indexOf(sourceImg), this.images.length - 1));
    this.paint();

    this.el.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.boundKeydown);
  }

  close() {
    if (!this.el || this.el.hidden) return;
    this.el.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.boundKeydown);
  }

  go(nextIndex) {
    const count = this.images.length;
    if (count === 0) return;
    this.index = ((nextIndex % count) + count) % count;
    this.paint();
  }

  paint() {
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    const prev = this.el.querySelector('[data-pmt-lightbox-prev]');
    const next = this.el.querySelector('[data-pmt-lightbox-next]');
    if (prev) prev.hidden = this.images.length < 2;
    if (next) next.hidden = this.images.length < 2;
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
    else if (event.key === 'ArrowLeft') this.go(this.index - 1);
    else if (event.key === 'ArrowRight') this.go(this.index + 1);
  }
}

if (!window.pmtLightbox) {
  window.pmtLightbox = new PmtReviewLightbox();
}

document.addEventListener('click', (event) => {
  const img = event.target.closest('.pmt-reviews__photos img');
  if (!img) return;
  const slide = img.closest('article.pmt-reviews__slide');
  if (!slide) return;
  event.preventDefault();
  window.pmtLightbox.open(slide, img);
});

/* Appstle wraps window.fetch and rewrites the /cart/add quantity, so /cart/add is routed
   through a pristine fetch held from a blank iframe. */
(function () {
  var nativeFetch = null;
  try {
    var frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.style.display = 'none';
    (document.documentElement || document.body).appendChild(frame);
    nativeFetch = frame.contentWindow && frame.contentWindow.fetch;
  } catch (error) {
    nativeFetch = null;
  }
  if (typeof nativeFetch !== 'function') return;

  function isCartAdd(input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    return /\/cart\/add(\.js)?([?#]|$)/.test(url);
  }

  var patched = null;

  function install() {
    if (window.fetch === patched) return;
    var previous = window.fetch;
    if (typeof previous !== 'function') return;
    patched = function pmtCartAddFetch(input, init) {
      if (isCartAdd(input)) {
        try {
          return nativeFetch(input, init);
        } catch (error) {
          /* fall through to the wrapped fetch */
        }
      }
      return previous.apply(this, arguments);
    };
    try {
      window.fetch = patched;
    } catch (error) {
      patched = null;
    }
  }

  install();
  var ticks = 0;
  var timer = window.setInterval(function () {
    install();
    if (++ticks > 40) window.clearInterval(timer);
  }, 500);
})();
